import { useContext, useEffect, useRef } from 'react';
import { UserContext } from '../UserContext';
import { getEmbedContext } from '../utils/embedContext';

const HEARTBEAT_MS = 30000;

/**
 * Logs one login per browser session, then heartbeats presence while the tab is open.
 */
export const usePresenceHeartbeat = () => {
	const { userInfo, apiCall } = useContext(UserContext);
	const loggedRef = useRef(false);

	useEffect(() => {
		if (!userInfo?.email || !apiCall) return undefined;

		const embed = getEmbedContext();
		const payload = {
			email: userInfo.email,
			personalInfo: {
				firstName: userInfo.personalInfo?.firstName || '',
				lastName: userInfo.personalInfo?.lastName || '',
				title: userInfo.personalInfo?.title || '',
			},
			...(embed.inIframe
				? {
						inIframe: true,
						parentOrigin: embed.parentOrigin,
						referrer: embed.referrer,
					}
				: {}),
		};

		const sessionKey = `gatesnow-login-logged:${userInfo.email.toLowerCase()}`;

		const sendLogin = async () => {
			if (loggedRef.current) return;
			if (sessionStorage.getItem(sessionKey) === '1') {
				loggedRef.current = true;
				return;
			}
			try {
				await apiCall('addlogin', payload, 'POST');
				sessionStorage.setItem(sessionKey, '1');
				loggedRef.current = true;
			} catch (err) {
				console.error('Failed to record GatesNow login', err);
			}
		};

		const sendHeartbeat = async () => {
			try {
				await apiCall('heartbeat', payload, 'POST');
			} catch (err) {
				console.error('Failed to send GatesNow heartbeat', err);
			}
		};

		sendLogin().finally(() => {
			sendHeartbeat();
		});

		const intervalId = setInterval(sendHeartbeat, HEARTBEAT_MS);
		const onVisibility = () => {
			if (document.visibilityState === 'visible') {
				sendHeartbeat();
			}
		};
		document.addEventListener('visibilitychange', onVisibility);

		return () => {
			clearInterval(intervalId);
			document.removeEventListener('visibilitychange', onVisibility);
		};
	}, [userInfo, apiCall]);
};
